import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  useTheme,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useForm } from "react-hook-form";
import Avatar from "@mui/material/Avatar";
import ImageIcon from "@mui/icons-material/ImageOutlined";
import UploadIcon from "@mui/icons-material/Upload";
import DeleteIcon from "@mui/icons-material/Delete";

import ConfirmationModal from "../../Modals/ConfirmationModal";
import ImageCropper from "../../../pages/Feed/ImageCropper";
import { PUT_WORKSPACE_IMAGE } from "../../../api/group";
import { GET_USER_BY_GROUPID } from "../../../api/user";
import { GET_IMAGE } from "../../../helpers/images";
import SoftInput from "../../SoftInput";
import SoftSelect from "../../SoftSelect";
import SoftButton from "../../SoftButton";
import SoftBox from "../../SoftBox";
import SoftTypography from "../../SoftTypography";

const SubWorkspacePanel = ({
  handleClose,
  handleSave,
  handleDelete,
  isLoading,
  types,
  group,
  parentWorkspace,
}) => {
  const { register, handleSubmit, reset, setValue, getValues, watch } = useForm({
    defaultValues: {
      image: "",
      name: "",
      typeId: parentWorkspace.typeId ? parentWorkspace.typeId : group.typeId,
      users: "",
      parentId: parentWorkspace._id,
      promoteTopLevel: false,
    },
  });

  const themeHook = useTheme();
  const {
    primary: { main: primaryColor },
  } = themeHook.palette;

  const imageInputRef = useRef();
  const [openDialog, setOpenDialog] = useState(false);
  const [imageSrc, setImageSrc] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [users, setUsers] = useState([]);
  const [fetching, setFetching] = useState(false);
  const hasKpis = useMemo(() => !!group?.kpis?.length, [group]);
  const promoteTopLevel = watch("promoteTopLevel");
  const isEditing = !!group?._id;

  const onSubmit = (data) => {
    const payload = { ...data };
    if (isEditing && data.promoteTopLevel) {
      payload.parentId = null;
    }
    delete payload.promoteTopLevel;
    handleSave(payload);
  };

  const handleUpload = () => {
    imageInputRef.current.value = null;
    imageInputRef.current.click();
  };

  const handleInputChange = async (e) => {
    if (e.target.files.length === 0) {
      return;
    }

    const imgSrc = e.target.files[0];
    if (imgSrc) {
      setImageSrc(URL.createObjectURL(imgSrc));
      setOpenDialog(true);
    }
  };

  const onSaveCroppedImage = async (croppedImageFile) => {
    if (!croppedImageFile) return;
    const formData = new FormData();
    setIsUploading(true);
    formData.append("image", croppedImageFile);
    PUT_WORKSPACE_IMAGE(formData)
      .then(({ data }) => {
        setImageSrc(data.key);
        setValue("image", data.key);
        setIsUploading(false);
      })
      .catch((err) => {
        console.log("Job image upload error", err.message);
        setIsUploading(false);
      });

    setOpenDialog(false);
    imageInputRef.current.value = null;
  };

  useEffect(() => {
    return () => {
      setIsUploading(false);
    };
  }, []);

  useEffect(() => {
    if (group) {
      reset({
        ...group,
        parentId: group.parentId || parentWorkspace?._id,
        promoteTopLevel: false,
      });
    }
  }, [group, parentWorkspace?._id, reset]);

  const fetchUsers = async () => {
    setFetching(true);
    try {
      const { data } = await GET_USER_BY_GROUPID(parentWorkspace._id);
      const options = data.map((item) => ({
        value: item._id,
        label: `${item.firstName} ${item.lastName}`,
      }));
      setUsers(options);
    } catch (err) {
      console.log(err);
    }
    setFetching(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  return (
    <>
      <SoftBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <SoftBox sx={{ display: "flex", gap: "0.5rem" }}>
          <SoftTypography variant="h4" sx={{ color: "#0F172A" }}>
            {group
              ? "Edit your sub-workspace"
              : `Create a new sub-workspace for ${parentWorkspace.name}`}
          </SoftTypography>
        </SoftBox>

        <SoftButton
          variant="outlined"
          color="dark"
          onClick={handleClose}
          sx={{
            "& svg": { fontSize: "1.25rem !important" },
            borderRadius: "50%",
            minWidth: "max-content",
            minHeight: "max-content",
            padding: "0.25rem",
          }}
        >
          <CloseRoundedIcon />
        </SoftButton>
      </SoftBox>
      <Divider
        sx={{
          backgroundColor: "rgba(15, 23, 42, 0.10)",
        }}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={1} className="mt-2">
          <Grid item mini={12} marginBottom={2}>
            <Grid container spacing={2} className="mt-2">
              <Grid item mini={5} marginBottom={2}>
                <SoftBox
                  sx={{
                    height: "130px",
                    maxWidth: "100%",
                    background: "#04549C1A",
                    border: "1.9px dashed #04549C",
                    borderRadius: "8px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                  }}
                >
                  {!isUploading && !getValues("image") ? (
                    <SoftBox
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 100, color: "#6C757D" }} />
                      <SoftTypography
                        sx={{
                          color: "#6C757D",
                          fontSize: "16px",
                          fontWeight: 600,
                        }}
                      >
                        Upload Logo
                      </SoftTypography>
                    </SoftBox>
                  ) : (
                    <SoftBox sx={{ width: "100%", height: "100%" }}>
                      <Avatar
                        sx={{
                          width: "100%",
                          height: "100%",
                        }}
                        variant="rounded"
                        src={
                          !isUploading && getValues("image")
                            ? GET_IMAGE(getValues("image"))
                            : ""
                        }
                      >
                        {isUploading && <CircularProgress />}
                      </Avatar>
                    </SoftBox>
                  )}

                  <input
                    ref={imageInputRef}
                    onInput={handleInputChange}
                    accept="image/*"
                    type="file"
                    hidden
                  />
                  <ImageCropper
                    imgSrc={imageSrc}
                    open={openDialog}
                    setOpen={setOpenDialog}
                    onSaveCroppedImage={onSaveCroppedImage}
                  />
                </SoftBox>
              </Grid>
              <Grid
                item
                mini={6}
                marginBottom={2}
                container
                alignItems="center"
              >
                <SoftBox>
                  <SoftTypography
                    sx={{ color: "#344054", fontSize: "16px", fontWeight: 500 }}
                  >
                    Upload Your Workspace Logo
                  </SoftTypography>
                  <SoftTypography
                    sx={{
                      color: "#98A2B3",
                      fontSize: "13px",
                      fontWeight: 400,
                      marginBottom: "10px",
                    }}
                  >
                    JPEG, PNG or GIF. Max file size 10GB
                  </SoftTypography>
                  <SoftButton
                    color="outlined"
                    variant="contained"
                    startIcon={<UploadIcon sx={{ color: "#475467" }} />}
                    onClick={handleUpload}
                    sx={{
                      textTransform: "none",
                      borderRadius: "4px",
                      border: "1px solid rgba(15, 23, 42, 0.12)",
                      boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.05)",
                      background: "rgba(15, 23, 42, 0.03)",
                      color: "#475467",
                      "&:hover": {
                        background: "rgba(15, 23, 42, 0.06)",
                      },
                    }}
                  >
                    Upload
                  </SoftButton>
                </SoftBox>
              </Grid>
            </Grid>
          </Grid>

          <Grid item mini={12} marginBottom={2}>
            <SoftBox
              sx={{
                marginBottom: "0.25rem",
              }}
            >
              <SoftTypography variant="button" sx={{ color: "#344054" }}>
                Enter Your Sub-Workspace Name
              </SoftTypography>
            </SoftBox>
            <SoftInput
              {...register("name")}
              variant="outlined"
              placeholder="Your sub-workspace name"
              sx={{
                border: "1px solid #E4E7EC",
                backgroundColor: "#F6F7F9 !important",
                color: "#1d2939 !important",
                "& .MuiInputBase-input::placeholder": {
                  color: "#98A2B3 !important",
                  opacity: "1",
                },
                "&.Mui-focused": {
                  borderColor: primaryColor,
                  boxShadow: `0rem 0rem 0rem 0.125rem ${primaryColor}`,
                },
              }}
            />
          </Grid>
          <Grid item mini={12} marginBottom={3}>
            <SoftBox
              sx={{
                marginBottom: "0.25rem",
              }}
            >
              <SoftTypography variant="button" sx={{ color: "#344054" }}>
                Select Workspace Type
              </SoftTypography>
            </SoftBox>
            <SoftSelect
              isLoading={fetching}
              options={users}
              name="users"
              // value={formik.values.kpimodels}
              onChange={(selectedOption) => {
                setValue("users", selectedOption);
              }}
              placeholder="Select a player (optional)"
              styles={{
                color: "#1d2939 !important",
                control: {
                  background: "#F6F7F9 !important",
                },
                placeholder: {
                  color: "#98A2B3 !important",
                  opacity: "1",
                },
                input: {
                  color: "#1d2939 !important",
                  opacity: "1",
                },
                singleValue: {
                  color: "#1d2939 !important",
                  opacity: "1",
                },
              }}
            />
          </Grid>
          {isEditing && (
            <Grid item mini={12} marginBottom={3}>
              <SoftBox
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                  padding: "12px 14px",
                  borderRadius: "8px",
                  border: "1px solid #E4E7EC",
                  background: "#F9FAFB",
                }}
              >
                <input
                  type="checkbox"
                  id="gmb-sub-promote"
                  checked={!!promoteTopLevel}
                  onChange={(e) => setValue("promoteTopLevel", e.target.checked)}
                  style={{ marginTop: 3, width: 16, height: 16, cursor: "pointer" }}
                />
                <SoftBox>
                  <SoftTypography
                    component="label"
                    htmlFor="gmb-sub-promote"
                    variant="button"
                    sx={{ color: "#344054", cursor: "pointer", display: "block" }}
                  >
                    Make top-level workspace again
                  </SoftTypography>
                  <SoftTypography
                    sx={{
                      color: "#98A2B3",
                      fontSize: "12px",
                      fontWeight: 400,
                      display: "block",
                      marginTop: "2px",
                    }}
                  >
                    Moves it out from under {parentWorkspace?.name || "its parent"} in the sidebar.
                    Scores and metrics stay the same.
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            </Grid>
          )}
        </Grid>

        <SoftBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <ConfirmationModal
            title={hasKpis ? "Disable Subworkspace" : "Delete Subworkspace"}
            question={
              hasKpis
                ? "This workspace has KPIs; both workspace and its KPIs will be disabled. Continue?"
                : "This workspace has no KPIs and would be permanently deleted. Continue?"
            }
            onSuccess={handleDelete}
            noText="No, cancel"
            yesText={hasKpis ? "Yes, disable" : "Yes, delete"}
          >
            <SoftButton
              color="outlined"
              variant="contained"
              sx={{
                textTransform: "none",
                borderRadius: "4px",
                border: "1px solid rgba(15, 23, 42, 0.12)",
                boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.05)",
                background: "rgba(15, 23, 42, 0.03)",
                color: "#475467",
                "&:hover": {
                  background: "rgba(15, 23, 42, 0.06)",
                },
              }}
            >
              Delete
            </SoftButton>
          </ConfirmationModal>
          <SoftBox display="flex" alignItems="center" gap="0.5rem">
            <SoftButton
              variant="contained"
              color="outlined"
              onClick={handleClose}
              sx={{
                textTransform: "none",
                borderRadius: "4px",
                border: "1px solid rgba(15, 23, 42, 0.12)",
                boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.05)",
                background: "rgba(15, 23, 42, 0.03)",
                color: "#475467",
                "&:hover": {
                  background: "rgba(15, 23, 42, 0.06)",
                },
              }}
            >
              BACK
            </SoftButton>
            <SoftButton
              variant="contained"
              color="primary"
              type="submit"
              disabled={isLoading}
              sx={{
                color: "primary.contrastText",
                textTransform: "none",
                borderRadius: "4px",
              }}
            >
              Save Sub-Workspace
              {isLoading && (
                <CircularProgress
                  color="inherit"
                  size="1rem"
                  className="ml-2"
                />
              )}
            </SoftButton>
          </SoftBox>
        </SoftBox>
      </form>
    </>
  );
};

export default SubWorkspacePanel;
